import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { collectPermissionCodes, fetchMyRbac } from '@/api/rbac';
import { useAuthStore } from '@/stores/auth';
import { registerPermissionsRevalidator, usePermissionsStore } from '@/stores/permissions';

/** 路由切换时，距上次成功拉取超过这个时长才重新对齐权限（避免每次点菜单都打一次 /me）。 */
const STALE_AFTER_MS = 60_000;
/** 403 触发的强制重拉的节流间隔——一屏并发多个请求同时 403 时只重拉一次。 */
const FORCE_THROTTLE_MS = 5_000;

/**
 * 已登录后拉取当前用户 RBAC 视图（菜单 + 权限码 + 角色 + super_admin 标志）。
 * 挂载在 RequireAuth 内，登录后自动触发。
 *
 * 三个触发时机：
 *   1. token 变化（登录 / 切换用户 / 刷新令牌）→ 强制拉；
 *   2. 路由切换且本地权限已过期（> {@link STALE_AFTER_MS}）→ 静默重新对齐。
 *      管理员改了角色授权后，在线用户不必重登也能在下一次跳页面时拿到新权限；
 *   3. 响应拦截器收到 403 → 前端以为有权限但后端拒绝，说明本地权限过期，立刻重拉。
 *
 * 首次拉取失败（404/网络）→ 退回 super_admin=true 兼容旧后端，console warn 提示。
 * 但**重新对齐时的失败不会降级**：已经拿到过真实权限集，就不能因为一次网络抖动
 * 把用户提权成 super_admin。
 */
export default function PermissionsBootstrap() {
  const token = useAuthStore((s) => s.accessToken);
  const setPermissions = usePermissionsStore((s) => s.setPermissions);
  const setDegradedFallback = usePermissionsStore((s) => s.setDegradedFallback);
  const clearPermissions = usePermissionsStore((s) => s.clear);
  const location = useLocation();

  const lastLoadedAtRef = useRef(0);
  const lastForcedAtRef = useRef(0);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    // 并发去重：路由切换与 403 可能同时触发，只跑一次。
    if (inFlightRef.current) return inFlightRef.current;

    const p = fetchMyRbac()
      .then((resp) => {
        // 请求飞行途中用户已登出 / 切了账号 → 这份响应属于上一个会话，丢弃。
        if (useAuthStore.getState().accessToken !== token) return;
        // /me 顶层 permissions 字段已下线，权限码挂在 menus 树的节点 permission_code 上。
        // 同时把整棵 menus 树存进 store，供 BasicLayout 动态渲染侧边栏。
        const menus = resp.menus ?? [];
        setPermissions(collectPermissionCodes(menus), !!resp.super_admin, menus, {
          dataScope: resp.data_scope ?? 'PLATFORM',
          companyId: resp.company_id ?? null,
        });
        lastLoadedAtRef.current = Date.now();
      })
      .catch((err) => {
        if (useAuthStore.getState().accessToken !== token) return;
        console.warn('[permissions] /admin/rbac/me 失败：', err);
        // 失败也记一次时间戳：否则接口持续不通时，每切一次路由都会再打一发。
        lastLoadedAtRef.current = Date.now();
        // 仅首次失败降级为 super_admin（兼容没有该接口的旧后端）。
        // 已加载过真实权限集时保持原状，等下一次触发再对齐。
        if (!usePermissionsStore.getState().loaded) {
          setDegradedFallback();
        }
      })
      .finally(() => {
        inFlightRef.current = null;
      });

    inFlightRef.current = p;
    return p;
  }, [token, setPermissions, setDegradedFallback]);

  // 1. token 变化 → 清空并强制重拉（不再用 loaded 短路，否则切换用户后会一直显示上一个用户的菜单）。
  useEffect(() => {
    if (!token) {
      clearPermissions();
      lastLoadedAtRef.current = 0;
      return;
    }
    lastLoadedAtRef.current = 0;
    void load();
  }, [token, clearPermissions, load]);

  // 2. 路由切换 → 权限过期才重新对齐。
  useEffect(() => {
    if (!token) return;
    if (Date.now() - lastLoadedAtRef.current < STALE_AFTER_MS) return;
    void load();
  }, [location.pathname, token, load]);

  // 3. 403 → 强制重拉（带节流）。
  useEffect(
    () =>
      registerPermissionsRevalidator(() => {
        const now = Date.now();
        if (now - lastForcedAtRef.current < FORCE_THROTTLE_MS) return;
        lastForcedAtRef.current = now;
        lastLoadedAtRef.current = 0;
        void load();
      }),
    [load],
  );

  return null;
}
